import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, Presentation } from 'lucide-react';

/**
 * Markdown + GFM for chat-style messages (matches Agent Demo styling).
 * Safe by default: no raw HTML (react-markdown escapes HTML).
 */
const markdownComponents = {
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 border-b border-gray-200">{children}</th>
  ),
  td: ({ children }) => <td className="px-3 py-2 text-gray-700 border-b border-gray-100">{children}</td>,
  h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-gray-800 mt-3 mb-1">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
  li: ({ children }) => <li className="text-gray-700">{children}</li>,
  p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-sylvan-200 pl-3 my-2 text-gray-600 italic">{children}</blockquote>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
  img: ({ src, alt, title }) => (
    <img
      src={src}
      alt={alt || ''}
      title={title}
      className="max-w-full max-h-96 rounded-lg my-2 border border-gray-200 object-contain"
      loading="lazy"
    />
  ),
  a: ({ href, children }) => {
    if (!href || href === '#') return <span className="text-indigo-600">{children}</span>;
    const isAgentDownload = href.includes('/api/agent-demo/download/');
    const isAgentView = href.includes('/api/agent-demo/view/');
    const isFilebrainDownload = href.includes('/api/project-ops/download-filebrain/');
    if (isAgentDownload || isFilebrainDownload) {
      return (
        <a
          href={href}
          download
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors cursor-pointer no-underline"
        >
          <FileText className="w-3.5 h-3.5" />
          {children}
        </a>
      );
    }
    if (isAgentView) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors cursor-pointer no-underline"
        >
          <Presentation className="w-3.5 h-3.5" />
          {children}
        </a>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline break-all">
        {children}
      </a>
    );
  },
  code: ({ inline, children }) =>
    inline ? (
      <code className="px-1.5 py-0.5 bg-gray-100 text-indigo-700 rounded text-xs font-mono">{children}</code>
    ) : (
      <pre className="bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto text-xs font-mono my-2">
        <code>{children}</code>
      </pre>
    ),
};

export default function MarkdownChatContent({ children, className = '' }) {
  const text = typeof children === 'string' ? children : '';
  return (
    <div className={`text-sm prose prose-sm max-w-none text-gray-800 break-words ${className}`.trim()}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
